import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
} from '../../../shared-functions/LocalStorage';
import { passwordValidation } from '../../../shared-functions/Validation';
// import OtpVerifyBanner from '../../login/components/OtpVerifyBanner';
import auth from '../../../utils/api/auth';

/**
 * Reset password component
 * @returns {React.JSX.Element} The rendered Reset password component.
 * @description Reset password component is a component for reset password functionality.
 */
function ResetPassword({ token }: { readonly token: string | undefined }): React.JSX.Element {
  const { t, i18n } = useTranslation('loginV2');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const passwordResetDetails = getLocalStorageItem('passwordReset');
  const [credentials, setCredentials] = useState<{
    password: string;
    confirmPassword: string;
  }>({
    password: '',
    confirmPassword: '',
  });

  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    hint: true,
    buttonLoader: false,
  });

  /**
   * Get input change value and store in credentials state
   * @param name
   * @param changeValue
   */
  const handleCredentialChange = (name: string, changeValue: string | number) => {
    setCredentials({
      ...credentials,
      [name]: changeValue,
    });
  };

  /**
   * Function to submit entered cre
   */
  const handleSubmit = () => {
    setFormStatus({
      showFormErrors: true,
      hint: false,
      buttonLoader: false,
    });
    if (
      passwordValidation(credentials.password) &&
      credentials.password === credentials.confirmPassword
    ) {
      setFormStatus({
        showFormErrors: true,
        hint: false,
        buttonLoader: true,
      });

      let requestBody = {};
      if (!token) {
        requestBody = {
          countryCode: passwordResetDetails.countryCode,
          accountCustomId: passwordResetDetails.accountCustomId
            ? passwordResetDetails.accountCustomId
            : undefined,
          emailOrPhoneNumber: passwordResetDetails.emailOrPhoneNumber,
          otp: passwordResetDetails.otp,
          newPassword: credentials.password,
        };
      } else {
        requestBody = {
          token,
          newPassword: credentials.password,
        };
      }

      const action = token ? auth.resetPasswordToken(requestBody) : auth.resetPassword(requestBody);

      action
        .then(() => {
          navigate('../login/credentials');
          removeLocalStorageItem('passwordReset');
          setFormStatus({
            showFormErrors: false,
            hint: false,
            buttonLoader: false,
          });
        })
        .catch((error) => {
          console.error('Login attempt failed:', error);
          setFormStatus({
            showFormErrors: false,
            hint: false,
            buttonLoader: false,
          });
          // Display a user-friendly message
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || t('somethingWentWrong'),
              },
            }),
          );
        });
    }
  };

  useEffect(() => {
    if (!passwordResetDetails && !token) {
      navigate('../login/forgot-password');
    }
  }, []);

  return (
    <div className="login-v2-wrapper" dir={i18n.dir()}>
      <CustomInput
        inputLabel={t('createNewPassword')}
        type="password"
        placeholder={t('enterPassword')}
        name="password"
        value={credentials.password}
        onChangeValue={handleCredentialChange}
        error={!passwordValidation(credentials.password) && formStatus.showFormErrors}
        errorMessage={t('passwordValidationRequirements')}
        hint={
          credentials.password.length === 0 || !passwordValidation(credentials.password)
            ? formStatus.hint
            : false
        }
        hintMessage={t('passwordValidationHint')}
      />
      <CustomInput
        inputLabel={t('confirmPassword')}
        type="password"
        placeholder={t('enterPassword')}
        name="confirmPassword"
        value={credentials.confirmPassword}
        onChangeValue={handleCredentialChange}
        error={!(credentials.password === credentials.confirmPassword) && formStatus.showFormErrors}
        errorMessage={t('passwordAndConfirmMustMatch')}
      />
      <MyButton
        label={t('submit')}
        buttonType="submit"
        onClickFunc={handleSubmit}
        loading={formStatus.buttonLoader}
        disableButton={formStatus.buttonLoader}
      />
    </div>
  );
}

export default ResetPassword;
